import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { useSession } from 'next-auth/react'

import { useAppDispatch } from '#/app/store/store'
import { getUserBalance } from '#/entities/balance'
import { Message, MessageSend } from '#/entities/message'
import { getApiUrl } from '#/shared/lib/constants'
import { Variant } from '#/shared/lib/hooks/use-show-data'
import { Device } from '#/shared/lib/types/entities'
import { IMessageRequest } from '#/shared/lib/types/types-gpt'
import { moveChatToTop } from '#/features/chats'

const formDataHelper = (file: File, dataForSend: MessageSend<any>): FormData => {
	const FD = new FormData()
	FD.append('file', file)
	FD.append('info', JSON.stringify(dataForSend.info))
	FD.append('content', JSON.stringify(dataForSend.content))

	return FD
}

export const ModelsWithChatsEndpoints = {
	getData: async (chatUid: string, offset: number, token?: string) => {
		try {
			const { data } = await axios.get<Message[]>(getApiUrl() + `/chats/${chatUid}/messages/?limit=10&offset=${offset}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			return data
		} catch (err: any) {
			return {
				error: true,
				message: 'Произошла ошибка при выполнении запроса',
				details: err as AxiosError,
			}
		}
	},
	sendData: async <T>(chatUid: string | null, dataForSend: MessageSend<T> | FormData, token?: string) => {
		const HeaderDataType = dataForSend instanceof FormData ? 'multipart/form-data' : 'application/json'

		return await axios.post<IMessageRequest, AxiosResponse<Message[] | { detail: string }>>(
			getApiUrl() + `/chats/${chatUid}/messages/`,
			dataForSend,
			{
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': HeaderDataType,
				},
			}
		)
	},
}

export function useModel(
	currentChat: string | null,
	showMessage: (message: string, variant?: Variant) => void,
	modelType: string,
	clearInput?: () => void
) {
	const { data } = useSession()
	const [messages, setMessages] = useState<Message[] | null>(null)
	const [loading, setLoading] = useState<boolean>(false)
	const dispatch = useAppDispatch()
	const [offset, setOffset] = useState<number>(0)

	useEffect(() => {
		if (currentChat) {
			setMessages([])
			;(async () => {
				setLoading(true)
				const answer = await ModelsWithChatsEndpoints.getData(currentChat, 0, data?.access)
				setLoading(false)

				if (Array.isArray(answer)) {
					setMessages(answer.reverse())
					setOffset(answer.length)
				} else {
					showMessage('Ошибка загрузки чата')
					return
				}
			})()
		}
	}, [currentChat])

	const getMessagesPagination = async () => {
		if (currentChat) {
			setLoading(true)
			const answer = await ModelsWithChatsEndpoints.getData(currentChat, offset, data?.access)
			setLoading(false)

			if (Array.isArray(answer) && messages != null) {
				const newMessages = answer.reverse()
				setMessages([...newMessages, ...messages])
				setOffset((prev) => prev + answer.length)
			} else {
				showMessage('Ошибка загрузки сообщений')
				return
			}
		}
	}

	const sendMessage = async <T>(dataForSend: MessageSend<T>) => {
		if (loading) {
			return
		}

		const date = new Date()

		const userMessage: Message = {
			content: dataForSend.content.trim(),
			info: dataForSend.info as any,
			is_sent: true,
			model: '',
			file: dataForSend.file ? ((URL.createObjectURL(dataForSend.file) + '?type=.' + dataForSend.file.name.split('.')[1]) as string) : null,
			from_model: false,
			uid: 'new-send',
			elapsed_time: '',
			is_favourite: false,
			created_at: date.toISOString(),
		}

		const modelMessageAboutStartGeneration: Message = {
			...userMessage,
			content: `Ваш вопрос получен. Ожидание ответа от ${modelType}...`,
			from_model: true,
			file: null,
			created_at: date.toISOString(),
		}

		setLoading(true)

		setMessages((prev) => [...prev!, userMessage, modelMessageAboutStartGeneration])

		// Перемещаем чат вверх сразу при отправке сообщения
		if (currentChat) {
			dispatch(moveChatToTop(currentChat))
		}

		let formData

		if (dataForSend.file) {
			const FD = new FormData()
			if (dataForSend.file) {
				FD.append('file', dataForSend.file)
			}
			FD.append('content', dataForSend.content)
			FD.append('info', JSON.stringify(dataForSend.info))
			formData = FD
		}

		const { data: result, status } = await ModelsWithChatsEndpoints.sendData(currentChat, formData ?? dataForSend, data?.access)

		setMessages((prev) => prev!.slice(0, -2))

		setLoading(false)


		if (status >= 400) {
			userMessage.is_sent = false
			setMessages((prev) => [...prev!, userMessage])
			const error = result as { detail: string }
			if (error.detail) return showMessage(error.detail)
			showMessage('Непредвиденная ошибка, попробуйте еще раз')
		} else {
			setMessages((prev) => [...prev!, ...(result as Message[])])
		}

		dispatch(getUserBalance(data?.access))
	}

	const deleteMessage = (message_uid: string) => {
		axios.delete(getApiUrl() + `/chats/${currentChat}/messages/${message_uid}`, {
			headers: {
				Authorization: `Bearer ${data?.access}`,
			},
		}).then(() => {
			if (messages) setMessages(messages?.filter((el) => el.uid !== message_uid))
		})
	}

	return { messages, sendMessage, loading, getMessagesPagination, deleteMessage }
}

export const ModelsWithImagesEndpoints = {
	getData: async (type: string, offset: number, token?: string) => {
		try {
			const { data } = await axios.get<Message[]>(getApiUrl() + `/media/image/${type}?limit=10&offset=${offset}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			return data
		} catch (err: any) {
			return {
				error: true,
				message: 'Произошла ошибка при выполнении запроса',
				details: err as AxiosError,
			}
		}
	},
	sendData: async <T>(type: string | null, dataForSend: MessageSend<T> | FormData, token?: string) => {
		const HeaderDataType = dataForSend instanceof FormData ? 'multipart/form-data' : 'application/json'

		try {
			const { data } = await axios.post<IMessageRequest, AxiosResponse<Message[]>>(getApiUrl() + `/media/image/${type}`, dataForSend, {
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': HeaderDataType,
				},
			})

			return data
		} catch (err: any) {
			return {
				error: true,
				message: 'Произошла ошибка при выполнении запроса',
				details: err as AxiosError,
			}
		}
	},
}

export function useModelImages<T>(showMessage: (message: string) => void, type: string, device: Device) {
	const { data } = useSession()

	const { getData, sendData } = ModelsWithImagesEndpoints

	const [messages, setMessages] = useState<Message[]>([])

	const [loading, setLoading] = useState<boolean>(false)

	const [isComplete, setIsComplete] = useState<boolean>(false)

	const [offset, setOffset] = useState<number>(0)

	const dispatch = useAppDispatch()

	const typeRef = useRef(type)
	const dataRef = useRef(data)
	const messagesRef = useRef(messages)
	const offsetRef = useRef(offset)

	useEffect(() => {
		typeRef.current = type
	}, [type])
	useEffect(() => {
		dataRef.current = data
	}, [data])
	useEffect(() => {
		messagesRef.current = messages
	}, [messages])
	useEffect(() => {
		offsetRef.current = offset
	}, [offset])

	useEffect(() => {
		if (data?.access && type !== '' && type !== undefined) {
			;(async () => {
				setLoading(true)
				const answer = await getData(type, offset, data?.access)
				setLoading(false)
				if ('error' in answer) {
					showMessage('Ошибка загрузки чата')
					return
				}

				if (Array.isArray(answer)) {
					if (device === 'desktop') {
						setMessages(answer)
						setOffset(answer.length)
					} else {
						setMessages(answer.reverse())
						setOffset(answer.length)
					}
				}
			})()
		}
	}, [data?.access, type])

	const getMessagesPagination = useCallback(
		async (device: 'mobile' | 'desktop') => {

			if (dataRef.current?.access && typeRef.current !== '' && typeRef.current !== undefined) {
				setLoading(true)
				const answer = await getData(typeRef.current, offsetRef.current, dataRef.current?.access)
				setLoading(false)

				if (Array.isArray(answer) && messagesRef.current != null && device === 'mobile') {
					const newMessages = answer.reverse()
					setMessages([...newMessages, ...messagesRef.current])
					setOffset((prev) => prev + answer.length)
				} else if (Array.isArray(answer) && messagesRef.current != null && device === 'desktop') {
					setMessages([...messagesRef.current, ...answer])
					setOffset((prev) => prev + answer.length)
				} else {
					showMessage('Ошибка загрузки сообщений')
					return
				}
			}
		},
		[data?.access, type, offset, messages]
	)

	const createImage = async <T>(dataForSend: MessageSend<T>) => {
		const { content, file } = dataForSend

		setIsComplete(false)
		setLoading(true)

		const dataSending = file ? formDataHelper(file, dataForSend) : dataForSend

		const result = await sendData(type, dataSending, data?.access)

		setLoading(false)

		if (result.hasOwnProperty('error')) {
			//@ts-ignore
			const message = (result.details as AxiosError).response.data.trim() ?? 'Ошибка отправки сообщения'
			showMessage(message)
			return
		}
		dispatch(getUserBalance(data?.access))

		//@ts-ignore
		setMessages((prev) => {
			if (prev?.length) {
				if (device === 'desktop') {
					return [...(result as Message[]), ...prev]
				} else {
					return [...prev, ...(result as Message[])]
				}
			}
			return result
		})

		setIsComplete(true)
	}

	return { messages, createImage, loading, isComplete, getMessagesPagination }
}

export const ModelsMediaApi = {
	getData: async (type: string, token?: string) => {
		try {
			const { data } = await axios.get<Message[]>(getApiUrl() + `/media/audio/${type}`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			return data
		} catch (err: any) {
			return {
				error: true,
				message: 'Произошла ошибка при выполнении запроса',
				details: err as AxiosError,
			}
		}
	},
	sendData: async <T>(type: string | null, dataForSend: MessageSend<T> | FormData, token?: string) => {
		try {
			const { data } = await axios.post<IMessageRequest, AxiosResponse<Message[]>>(getApiUrl() + `/media/audio/${type}`, dataForSend, {
				withCredentials: true,
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})

			return data
		} catch (err: any) {
			return {
				error: true,
				message: 'Произошла ошибка при выполнении запроса',
				details: err as AxiosError,
			}
		}
	},
}

export function useMedia(showMessage: (message: string) => void, modelType: string) {
	const { data } = useSession()

	const [messages, setMessages] = useState<Message[] | null>(null)

	const [loading, setLoading] = useState<boolean>(false)

	const dispatch = useAppDispatch()

	const [input, setInput] = React.useState<string>('')

	useEffect(() => {
		if (data?.access) {
			;(async () => {
				setLoading(true)
				const answer = await ModelsMediaApi.getData(modelType, data?.access)
				setLoading(false)
				if ('error' in answer) {
					showMessage('Ошибка загрузки')
					return
				}

				if (Array.isArray(answer)) setMessages(answer.reverse())
			})()
		}
	}, [data?.access])

	const sendMessage = async <T>(dataForSend: MessageSend<T>) => {
		if (input.trim() === '') {
			showMessage('Введите запрос!')
			return
		}

		if (loading) {
			return
		}

		const userMessage: Message = {
			content: input,
			info: null,
			is_sent: true,
			model: '',
			file: null,
			from_model: false,
			uid: 'new-send',
			elapsed_time: '',
			is_favourite: false,
			created_at: '111',
		}
		const modelMessageAboutStartGeneration: Message = {
			...userMessage,
			content: `Ваш вопрос получен. Ожидание ответа от ${modelType}...`,
			from_model: true,
			created_at: '222',
		}

		setLoading(true)

		setMessages((prev) => [...prev!, userMessage, modelMessageAboutStartGeneration])

		const result = await ModelsMediaApi.sendData(modelType, dataForSend, data?.access!)

		setMessages((prev) => prev!.slice(0, -2))

		setLoading(false)

		if (result.hasOwnProperty('error')) {
			try {
				//@ts-ignore
				const message = (result.details as AxiosError).response.data.trim() ?? 'Ошибка отправки сообщения'
				showMessage(message)
				return
			} catch (e) {
				return
			}
		}

		setMessages((prev) => [...prev!, ...(result as Message[])])
		setInput('')
		dispatch(getUserBalance(data?.access))
	}

	return { messages, sendMessage, loading, input, setInput }
}
